/**
 * Servicio para gestionar eventos de Facebook Pixel
 * 
 * Este servicio proporciona una capa de abstracción para interactuar
 * con Facebook Pixel, permitiendo analizar y optimizar el embudo de conversión.
 */

import { PIXEL_ID, PIXEL_CONFIG } from './pixelConfig';

// Definición de eventos estándar y personalizados
export const PIXEL_EVENTS = {
  // Eventos estándar de Facebook
  PAGE_VIEW: 'PageView',
  COMPLETE_REGISTRATION: 'CompleteRegistration',
  CONTACT: 'Contact',
  LEAD: 'Lead',
  PURCHASE: 'Purchase',
  
  // Eventos personalizados para la aplicación
  SCAN_INITIATED: 'ScanInitiated',
  SCAN_PROGRESS: 'ScanProgress',
  SCAN_COMPLETED: 'ScanCompleted',
  REPORT_VIEWED: 'ReportViewed',
  PLAN_VIEWED: 'PlanViewed',
  CHECKOUT_INITIATED: 'CheckoutInitiated',
  LOADER_DISPLAYED: 'LoaderDisplayed',
  
  // Nuevos eventos para el demo
  DEMO_VIEWED: 'DemoViewed',
  DEMO_DOG_SELECTED: 'DemoDogSelected',
  DEMO_ANALYSIS_STARTED: 'DemoAnalysisStarted',
  DEMO_ANALYSIS_COMPLETED: 'DemoAnalysisCompleted',
  DEMO_TO_REGISTER: 'DemoToRegister'
};

/**
 * Inicializa Facebook Pixel si aún no se ha inicializado
 * @param {string} pixelId - ID del Pixel (opcional, usa el valor por defecto si no se proporciona)
 */
export const initializePixel = (pixelId = PIXEL_ID) => {
  if (typeof window !== 'undefined') {
    // Simple check to prevent multiple initializations
    if (window.fbq) {
      return;
    }
    
    // Añadir el script de Facebook Pixel
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    
    // Inicializar con el ID de Pixel configurado
    window.fbq('init', pixelId);
    console.log(`[Facebook Pixel] Inicializado con ID: ${pixelId}`);
    
    // Rastrear la vista de página inicial
    window.fbq('track', 'PageView');
  }
};

/**
 * Rastrea un evento de Facebook Pixel
 * @param {string} eventName - Nombre del evento a rastrear
 * @param {Object} params - Parámetros adicionales para el evento
 */
export const trackPixelEvent = (eventName, params = {}) => {
  // Asegurarse de que el Pixel esté inicializado
  initializePixel();
  
  try {
    if (typeof window !== 'undefined' && window.fbq) {
      // Enviar el evento a Facebook
      window.fbq('track', eventName, params);
      
      console.log(`[Facebook Pixel] Evento rastreado: ${eventName}`, params);
      return true;
    }
  } catch (error) {
    console.error(`[Facebook Pixel] Error al rastrear evento ${eventName}:`, error);
    return false;
  }
  
  console.warn('[Facebook Pixel] fbq no está disponible');
  return false;
};

/**
 * Rastrea una vista de página
 * @param {Object} params - Parámetros adicionales
 */
export const trackPageView = (params = {}) => {
  trackPixelEvent(PIXEL_EVENTS.PAGE_VIEW, params);
};

/**
 * Rastrea un registro completado
 * @param {string} method - Método de registro (email, google, etc)
 */
export const trackRegistration = (method = 'website', params = {}) => {
  trackPixelEvent(PIXEL_EVENTS.COMPLETE_REGISTRATION, {
    content_name: 'User Registration',
    status: true,
    method,
    ...params
  });
};

/**
 * Rastrea el inicio de un escaneo/análisis
 * @param {Object} params - Parámetros del análisis
 */
export const trackScanInitiated = (params = {}) => {
  trackPixelEvent(PIXEL_EVENTS.SCAN_INITIATED, {
    content_name: 'Pet Analysis',
    content_type: 'basic',
    content_category: 'pet_health',
    ...params
  });
};

/**
 * Rastrea un análisis completado
 * @param {Object} params - Parámetros del análisis
 */
export const trackScanCompleted = (params = {}) => {
  trackPixelEvent(PIXEL_EVENTS.SCAN_COMPLETED, {
    content_name: 'Pet Analysis',
    content_type: 'basic',
    content_category: 'pet_health',
    success: true,
    ...params
  });
};

/**
 * Rastrea cuando un usuario ve un reporte
 * @param {string} reportId - ID del reporte
 * @param {Object} params - Parámetros adicionales
 */
export const trackReportViewed = (reportId, params = {}) => {
  trackPixelEvent(PIXEL_EVENTS.REPORT_VIEWED, {
    content_name: 'Pet Health Report',
    content_id: reportId,
    content_type: 'report',
    ...params
  });
};

/**
 * Rastrea cuando un usuario ve la página de planes
 * @param {string} source - Fuente desde donde llegó a la página de planes
 */
export const trackPlanViewed = (source = 'direct', params = {}) => {
  trackPixelEvent(PIXEL_EVENTS.PLAN_VIEWED, {
    content_name: 'Subscription Plans',
    content_category: 'pricing',
    source,
    ...params
  });
};

/**
 * Rastrea cuando un usuario inicia el proceso de pago
 * @param {string} planId - ID del plan seleccionado
 * @param {number} value - Valor del plan
 * @param {string} currency - Moneda (por defecto MXN)
 */
export const trackCheckoutInitiated = (planId, value, currency = 'MXN', params = {}) => {
  trackPixelEvent(PIXEL_EVENTS.CHECKOUT_INITIATED, {
    content_name: `Plan: ${planId}`,
    content_type: 'subscription',
    content_ids: [planId],
    value,
    currency,
    ...params
  });
};

/**
 * Rastrea una compra completada
 * @param {string} planId - ID del plan comprado
 * @param {number} value - Valor del plan
 * @param {string} orderId - ID de la orden/transacción
 * @param {string} currency - Moneda (por defecto MXN)
 */
export const trackPurchase = (planId, value, orderId, currency = 'MXN', params = {}) => {
  trackPixelEvent(PIXEL_EVENTS.PURCHASE, {
    content_name: `Plan: ${planId}`,
    content_type: 'subscription',
    content_ids: [planId],
    transaction_id: orderId,
    value,
    currency,
    ...params
  });
}; 